/**
 * CBWSetupFlow — shared sub-component for adding a Control By Web device.
 * Mounted from AccessControl.tsx (step 3 CBW branch) and Devices.tsx (step 'cbw').
 * NOT a new modal — rendered inside the caller's existing Modal chrome.
 *
 * States: form → provisioning → bundle → polling → done
 */
import React, { useState, useEffect, useRef } from 'react';
import { provisionCbw, getBundleUrl, pushCbwConfig, getCbwStatus, CbwProvisionResult } from '../../api/cbwApi';

export interface CBWSetupFlowProps {
  entryPoint: 'devices' | 'access-control';
  customerId: string;
  propertyId?: string;    // required for 'devices'; optional for 'access-control'
  acsSystemId?: string;   // for 'access-control' entry when adding to an existing CBW system
  properties?: any[];     // full properties list so we can show a picker if propertyId not supplied
  onSuccess: (result: { device_id: string; iot_thing_name: string }) => void;
  onCancel: () => void;
  onError?: (err: string) => void;
}

type FlowState = 'form' | 'provisioning' | 'bundle' | 'polling' | 'done';

const POLL_INTERVAL_MS = 15_000;
const POLL_TIMEOUT_MS  = 10 * 60 * 1000; // 10 minutes

export const CBWSetupFlow: React.FC<CBWSetupFlowProps> = ({
  entryPoint,
  customerId,
  propertyId: propIdProp,
  acsSystemId,
  properties = [],
  onSuccess,
  onCancel,
  onError,
}) => {
  const [flowState, setFlowState]           = useState<FlowState>('form');
  const [displayName, setDisplayName]       = useState('');
  const [selectedPropId, setSelectedPropId] = useState(propIdProp || '');
  const [formError, setFormError]           = useState('');
  const [result, setResult]                 = useState<CbwProvisionResult | null>(null);
  const [pushUrl, setPushUrl]               = useState('');
  const [pushUser, setPushUser]             = useState('');
  const [pushPass, setPushPass]             = useState('');
  const [pushLoading, setPushLoading]       = useState(false);
  const [pushError, setPushError]           = useState('');
  const [pushSuccess, setPushSuccess]       = useState(false);
  const [showAdvanced, setShowAdvanced]     = useState(false);
  const [pollStatus, setPollStatus]         = useState<string>('Waiting for device to connect…');
  const [timedOut, setTimedOut]             = useState(false);

  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  // ---------- provision -------------------------------------------------------

  const handleProvision = async () => {
    const pid = selectedPropId || propIdProp || '';
    if (!displayName.trim()) { setFormError('Device name is required.'); return; }
    if (!pid) { setFormError('Please select a property.'); return; }
    setFormError('');
    setFlowState('provisioning');
    try {
      const res = await provisionCbw({
        property_id: pid,
        display_name: displayName.trim(),
        entry_point: entryPoint,
        acs_system_id: acsSystemId,
      });
      setResult(res);
      setFlowState('bundle');
    } catch (e: any) {
      const msg = e?.message || 'Provision failed';
      setFlowState('form');
      setFormError(msg);
      onError?.(msg);
    }
  };

  // ---------- push config (Path A) -------------------------------------------

  const handlePush = async () => {
    if (!result) return;
    setPushError('');
    setPushLoading(true);
    try {
      await pushCbwConfig({ device_id: result.device_id, cbw_url: pushUrl, cbw_admin_user: pushUser, cbw_admin_password: pushPass });
      setPushSuccess(true);
    } catch (e: any) {
      setPushError(e?.message || 'Push failed');
    } finally {
      setPushLoading(false);
    }
  };

  // ---------- polling --------------------------------------------------------

  const startPolling = () => {
    if (!result) return;
    setFlowState('polling');
    startTimeRef.current = Date.now();
    setPollStatus('Waiting for device to connect…');
    setTimedOut(false);

    const tick = async () => {
      if (document.hidden) return; // pause while tab is hidden — mirror Devices.tsx pattern
      try {
        const s = await getCbwStatus(result.device_id);
        if (s.online) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setPollStatus('Device connected!');
          setFlowState('done');
          return;
        }
        if (s.last_seen_at) {
          setPollStatus(`Last seen: ${new Date(s.last_seen_at).toLocaleTimeString()} — waiting for NBIRTH…`);
        }
      } catch {
        // non-fatal — keep polling
      }
      if (Date.now() - startTimeRef.current > POLL_TIMEOUT_MS) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setTimedOut(true);
        setPollStatus('Timeout — device did not connect within 10 minutes.');
      }
    };

    intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);
    tick(); // immediate first check
  };

  // ---------- done auto-fire -------------------------------------------------

  useEffect(() => {
    if (flowState === 'done' && result) {
      const t = setTimeout(() => onSuccess({ device_id: result.device_id, iot_thing_name: result.iot_thing_name }), 2000);
      return () => clearTimeout(t);
    }
  }, [flowState, result, onSuccess]);

  // ---------- cleanup --------------------------------------------------------

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // ---------- render: form ----------------------------------------------------

  if (flowState === 'form') {
    const showPropPicker = !propIdProp && properties.length > 0;
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Provision a <strong>Control By Web</strong> relay device. You'll download a config bundle and import it via the CBW web UI.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Device Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="e.g., Front Gate Relay"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus
          />
        </div>
        {showPropPicker && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property <span className="text-red-500">*</span></label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              value={selectedPropId}
              onChange={(e) => setSelectedPropId(e.target.value)}
            >
              <option value="">— Select a property —</option>
              {properties.map((p: any) => (
                <option key={p.uuid || p.id} value={p.uuid || p.id}>
                  {p.name}{p.address ? ` — ${p.address}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {formError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
        )}
        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleProvision}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!displayName.trim() || (!selectedPropId && !propIdProp && properties.length > 0)}
          >
            Provision Device
          </button>
        </div>
      </div>
    );
  }

  // ---------- render: provisioning -------------------------------------------

  if (flowState === 'provisioning') {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-4">
        <svg className="animate-spin h-8 w-8 text-brand-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <p className="text-sm text-gray-600">Creating device on Intrlock IoT&hellip;</p>
      </div>
    );
  }

  // ---------- render: bundle --------------------------------------------------

  if (flowState === 'bundle' && result) {
    const bundleUrl = getBundleUrl(result.bundle_token);
    return (
      <div className="space-y-5">
        {/* Success header */}
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
          <svg className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-green-800">Device provisioned</p>
            <p className="text-xs text-green-700 mt-0.5">IoT Thing: <span className="font-mono">{result.iot_thing_name}</span></p>
          </div>
        </div>

        {/* Download bundle — §28.518.98 fetch+blob so JWT header is sent */}
        <div>
          <button
            type="button"
            onClick={async () => {
              try {
                const tok = localStorage.getItem('intrlock_token') || '';
                const res = await fetch(bundleUrl, { headers: { Authorization: `Bearer ${tok}` } });
                if (!res.ok) {
                  const body = await res.text();
                  alert(`Bundle download failed (${res.status}): ${body.slice(0, 200)}`);
                  return;
                }
                const blob = await res.blob();
                const cd = res.headers.get('content-disposition') || '';
                const filename = cd.match(/filename="?([^";]+)/)?.[1] || 'cbw-bundle.zip';
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch (e: any) {
                alert(`Bundle download failed: ${e?.message || e}`);
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Download Import Bundle
          </button>
        </div>

        {/* Setup instructions */}
        <div>
          <p className="text-sm font-semibold text-gray-800 mb-2">Setup steps</p>
          <ol className="space-y-1.5 text-sm text-gray-700 list-decimal list-inside">
            <li>Unzip the bundle — it contains <span className="font-mono text-xs bg-gray-100 px-1 rounded">cert.pem</span>, <span className="font-mono text-xs bg-gray-100 px-1 rounded">private-key.pem</span>, <span className="font-mono text-xs bg-gray-100 px-1 rounded">intrlock-root-ca.pem</span>, and <span className="font-mono text-xs bg-gray-100 px-1 rounded">control-config.xml</span>.</li>
            <li>Log into your CBW device's web interface (default <span className="font-mono text-xs bg-gray-100 px-1 rounded">192.168.1.2</span>), go to <strong>Setup &rarr; Import/Export</strong>.</li>
            <li>Import <span className="font-mono text-xs bg-gray-100 px-1 rounded">control-config.xml</span> — this pre-loads the MQTT broker, port 8883, TLS, and your client ID.</li>
            <li>Upload the three certificate files under <strong>Setup &rarr; SSL/TLS Certificates</strong>, then reboot the device.</li>
          </ol>
        </div>

        {/* Advanced: Path A auto-push */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            <span>Or configure automatically (advanced)</span>
            <svg className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          {showAdvanced && (
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-gray-500">If the device is reachable from this browser (same LAN), Intrlock can push the config automatically.</p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">CBW device URL</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="http://192.168.1.2"
                  value={pushUrl}
                  onChange={(e) => setPushUrl(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Admin username</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="admin"
                    value={pushUser}
                    onChange={(e) => setPushUser(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Password"
                    value={pushPass}
                    onChange={(e) => setPushPass(e.target.value)}
                  />
                </div>
              </div>
              {pushError && <p className="text-xs text-red-600">{pushError}</p>}
              {pushSuccess && <p className="text-xs text-green-600">Config pushed successfully — device is rebooting.</p>}
              <button
                type="button"
                onClick={handlePush}
                disabled={pushLoading || !pushUrl || !pushUser}
                className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 rounded hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {pushLoading && (
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                )}
                Push to Device
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={startPolling}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
          >
            I&rsquo;ve imported the config &rarr; Wait for connection
          </button>
        </div>
      </div>
    );
  }

  // ---------- render: polling -------------------------------------------------

  if (flowState === 'polling') {
    return (
      <div className="space-y-4 py-4">
        <div className="flex items-center gap-3">
          {!timedOut && (
            <svg className="animate-spin h-5 w-5 text-brand-600 flex-shrink-0" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
          <p className="text-sm text-gray-700">{pollStatus}</p>
        </div>
        {!timedOut && (
          <p className="text-xs text-gray-500">
            Polling every 15 seconds. Device must have port 8883 outbound to the Intrlock IoT Gateway.
          </p>
        )}
        {timedOut && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1">
            <p className="font-semibold">Device did not connect within 10 minutes.</p>
            <p>Troubleshooting checklist:</p>
            <ul className="list-disc list-inside text-xs space-y-1 mt-1">
              <li>Confirm the device has internet access (try pinging 8.8.8.8 from the device).</li>
              <li>Verify outbound TCP 8883 is allowed by your firewall.</li>
              <li>Confirm DNS resolves the broker hostname in <span className="font-mono">control-config.xml</span></li>
              <li>Re-check that <span className="font-mono">control-config.xml</span> was imported and the device rebooted.</li>
              <li>Re-upload the three certificate files if the MQTT connection shows TLS errors in device logs.</li>
            </ul>
          </div>
        )}
        <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          {timedOut && (
            <button
              type="button"
              onClick={startPolling}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------- render: done ----------------------------------------------------

  if (flowState === 'done') {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-100 border-2 border-green-500">
          <svg className="h-7 w-7 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-900">Device connected!</p>
        <p className="text-sm text-gray-500">Closing in a moment&hellip;</p>
      </div>
    );
  }

  return null;
};
